/**
 * Auth client manages token lifecycle.
 * Access token stored in memory (JS closure) and optionally seeded from
 * localStorage on startup (used by E2E tests via Playwright storageState).
 * Refresh token handled via HTTP-only cookie (set by API).
 */
import { callEndpoint, auth, permissions as permissionsApi, ApiError } from '@ooh/contracts';
import type { EndpointDef } from '@ooh/contracts';
import type { UserProfile } from '@ooh/contracts';
import { API_BASE } from './api-base';
import { setSentryUser } from './sentry';
import { isPublicRoutePath } from './public-route';

export function endpointBaseUrl(path: string): string {
  // callEndpoint does simple string concatenation: baseUrl + path.
  // Endpoint paths fall into two families:
  //   - "/api/..."  paths (most import/media endpoints): path already carries
  //     the /api prefix, so we strip it from API_BASE to avoid doubling.
  //     Local dev: API_BASE='/api' → stripped='' → '/api/...' is proxied by Vite. ✓
  //     Cross-origin: API_BASE='https://api.railway.app/api' → stripped='https://api.railway.app' ✓
  //   - "/auth/..." paths (auth/permissions endpoints): no /api prefix in path,
  //     so full API_BASE is used as-is in both envs. ✓
  if (path.startsWith('/api/')) {
    return API_BASE.replace(/\/api$/, '');
  }
  return API_BASE;
}

/** Auth endpoint paths that must never be intercepted for 401 retry. */
const AUTH_PATHS: Set<string> = new Set([auth.login.path, auth.refresh.path, auth.logout.path]);

export interface AuthClient {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
  getAccessToken: () => string | null;
  getUser: () => UserProfile | null;
  getPermissions: () => string[] | null;
  /**
   * Ensures permissions are loaded. If already loaded, resolves immediately.
   * Called during route beforeLoad when a stored token bypasses the normal
   * login/refresh flow and permissions would otherwise remain null.
   */
  ensurePermissions: () => Promise<void>;
  /**
   * Calls an endpoint with the current access token attached as
   * `Authorization: Bearer <token>`. On 401, silently refreshes the token
   * and retries once. If refresh fails, clears auth state and re-throws.
   * Concurrent 401s coalesce behind a single refresh call.
   *
   * Accepts an optional `signal` (e.g. TanStack Query's `queryFn(context).signal`)
   * forwarded to `callEndpoint`/`fetch` so the caller can cancel the request.
   */
  callWithAuth: <TDef extends EndpointDef>(
    def: TDef,
    request: Parameters<typeof callEndpoint<TDef>>[1],
    options?: { signal?: AbortSignal },
  ) => ReturnType<typeof callEndpoint<TDef>>;
}

/**
 * Reads the access token from localStorage if it exists and is not yet expired.
 * Falls back to null so the normal refresh flow takes over.
 */
function readStoredToken(): string | null {
  try {
    const token = window.localStorage.getItem('accessToken');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]!)) as { exp?: number };
    if (typeof payload.exp === 'number' && payload.exp * 1000 > Date.now()) {
      return token;
    }
    return null;
  } catch {
    return null;
  }
}

// eslint-disable-next-line max-lines-per-function
export function createAuthClient(): AuthClient {
  let accessToken: string | null = readStoredToken();
  let currentUser: UserProfile | null = null;
  let currentPermissions: string[] | null = null;
  let refreshPromise: Promise<boolean> | null = null;

  function clearAuthState(): void {
    accessToken = null;
    currentUser = null;
    currentPermissions = null;
    setSentryUser(null);
  }

  async function fetchPermissions(): Promise<void> {
    try {
      const response = await callEndpoint(
        permissionsApi.getMyPermissions,
        {},
        {
          baseUrl: endpointBaseUrl(permissionsApi.getMyPermissions.path),
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        },
      );
      currentPermissions = response.permissions;
    } catch {
      // Non-fatal: keep permissions as null
    }
  }

  async function login(email: string, password: string): Promise<void> {
    const response = await callEndpoint(
      auth.login,
      { email, password },
      {
        baseUrl: endpointBaseUrl(auth.login.path),
      },
    );
    accessToken = response.accessToken;
    currentUser = response.user;
    setSentryUser(currentUser);
    await fetchPermissions();
  }

  async function logout(): Promise<void> {
    try {
      await callEndpoint(auth.logout, {}, { baseUrl: endpointBaseUrl(auth.logout.path) });
    } catch {
      // Always clear state even if the network call fails
    } finally {
      clearAuthState();
    }
  }

  async function doRefresh(): Promise<boolean> {
    try {
      const response = await callEndpoint(
        auth.refresh,
        {},
        {
          baseUrl: endpointBaseUrl(auth.refresh.path),
        },
      );
      accessToken = response.accessToken;
      currentUser = response.user;
      setSentryUser(currentUser);
      await fetchPermissions();
      return true;
    } catch {
      clearAuthState();
      return false;
    }
  }

  async function refresh(): Promise<boolean> {
    if (refreshPromise !== null) return refreshPromise;
    refreshPromise = doRefresh();
    try {
      return await refreshPromise;
    } finally {
      refreshPromise = null;
    }
  }

  function getAccessToken(): string | null {
    return accessToken;
  }

  function getUser(): UserProfile | null {
    if (currentUser !== null) return currentUser;
    // Decode user from the stored access token while background refresh is pending.
    // This ensures route guards can check user.roles synchronously on first load.
    if (accessToken !== null) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]!)) as {
          sub?: string;
          email?: string;
          roles?: string[];
        };
        if (payload.sub && payload.email && payload.roles?.length) {
          return {
            id: Number(payload.sub),
            email: payload.email,
            roles: payload.roles as UserProfile['roles'],
            name: payload.email,
            department: null,
          };
        }
      } catch {
        // malformed token — fall through to null
      }
    }
    return null;
  }

  function getPermissions(): string[] | null {
    return currentPermissions;
  }

  function authHeaders(): Record<string, string> {
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  }

  function redirectToLoginIfTokenExpired(): void {
    // Anonymous /public/* routes (e.g. the public offer page and its unit
    // detail screen) never carry a session, yet still render components that
    // unconditionally call an authenticated endpoint to fail open (e.g.
    // useHasPermission's capability check — client-media-unit-detail /
    // "Client mode hides edit and delete even when the permission hook fails
    // open"). Their resulting 401 must never hard-navigate an anonymous
    // visitor off the public page they are on. Mirrors the same bypass
    // __root.tsx's handleRootAuthGuard applies to the router-level auth guard
    // (isPublicRoutePath is the single source of truth for both gates).
    if (isPublicRoutePath(window.location.pathname)) {
      return;
    }

    // Only navigate to login if the stored access token is also expired.
    // A transient server-side 401 (e.g. Redis circuit breaker tripped,
    // authentication service temporarily unavailable) should NOT evict a
    // user who still has a valid access token — that would cause jarring
    // mid-session logouts.
    const tokenStillValid = readStoredToken() !== null;
    if (!tokenStillValid) {
      window.location.href = '/login';
    }
  }

  async function callWithAuth<TDef extends EndpointDef>(
    def: TDef,
    request: Parameters<typeof callEndpoint<TDef>>[1],
    options?: { signal?: AbortSignal },
  ): ReturnType<typeof callEndpoint<TDef>> {
    const isAuthEndpoint = AUTH_PATHS.has(def.path);

    try {
      return await callEndpoint(def, request, {
        baseUrl: endpointBaseUrl(def.path),
        headers: authHeaders(),
        signal: options?.signal,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401 && !isAuthEndpoint) {
        const refreshed = await refresh();
        if (!refreshed) {
          redirectToLoginIfTokenExpired();
          throw err;
        }
        return callEndpoint(def, request, {
          baseUrl: endpointBaseUrl(def.path),
          headers: authHeaders(),
          signal: options?.signal,
        });
      }
      throw err;
    }
  }

  // If a token was seeded from localStorage, kick off a background refresh to
  // populate currentUser from the server.
  // On 401: only clear auth state and redirect if the stored access token is also
  // expired. A 401 from the background refresh while the access token is still valid
  // indicates a token-rotation race (e.g. two tabs / parallel E2E workers using the
  // same storageState simultaneously). In that case the user can still navigate using
  // the valid access token; the next callWithAuth will handle expiry.
  // On any other error (5xx, network): keep the stored accessToken so the user can
  // still navigate; the next callWithAuth will retry and redirect if truly expired.
  if (accessToken !== null) {
    void callEndpoint(auth.refresh, {}, { baseUrl: endpointBaseUrl(auth.refresh.path) })
      .then((response) => {
        accessToken = response.accessToken;
        currentUser = response.user;
        setSentryUser(currentUser);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          // Only evict the session when the stored access token is also expired.
          // If it is still valid, the 401 is a rotation-race — ignore it.
          const tokenStillValid = readStoredToken() !== null;
          if (!tokenStillValid) {
            clearAuthState();
            window.location.href = '/login';
          }
        }
        // Non-401 errors (5xx, network blip): keep the stored accessToken.
        // The next callWithAuth will retry and redirect if the session is truly gone.
      });
  }

  async function ensurePermissions(): Promise<void> {
    if (currentPermissions === null && accessToken !== null) {
      // Use a short timeout so a slow API doesn't block route rendering indefinitely.
      // If permissions can't be loaded within 5s, fail-open (permissions stay null).
      await Promise.race([
        fetchPermissions(),
        new Promise<void>((resolve) => setTimeout(resolve, 5_000)),
      ]);
    }
  }

  return {
    login,
    logout,
    refresh,
    getAccessToken,
    getUser,
    getPermissions,
    ensurePermissions,
    callWithAuth,
  };
}
